import React from 'react';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { fieldClass, labelClass, secondaryBtnClass } from '../styles';
import BlobPicker from './BlobPicker';
import type { CoreOutputItem } from './types';

interface Props {
  value: CoreOutputItem[];
  configured: boolean;
  container?: string | null;
  onChange: (next: CoreOutputItem[]) => void;
}

const CoreOutputsEditor: React.FC<Props> = ({
  value,
  configured,
  container,
  onChange,
}) => {
  const updateAt = (index: number, patch: Partial<CoreOutputItem>) => {
    onChange(value.map((item, i) => (i === index ? { ...item, ...patch } : item)));
  };

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= value.length) return;
    const next = [...value];
    const tmp = next[index];
    next[index] = next[target];
    next[target] = tmp;
    onChange(next);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <label className={`${labelClass} mb-0`}>Core outputs</label>
        <button
          type="button"
          className={`${secondaryBtnClass} inline-flex items-center gap-1`}
          onClick={() =>
            onChange([...value, { name: '', text: '', path: '', image_url: '' }])
          }
        >
          <Plus className="w-3.5 h-3.5" />
          Add core output
        </button>
      </div>
      {value.length === 0 && (
        <p className="text-[12px] text-[#8A8A8A]">
          No core outputs yet. Add one to describe a deliverable and attach a file.
        </p>
      )}
      {value.map((item, index) => (
        <div
          key={index}
          className="border border-[#E8E8E8] rounded-lg p-3 space-y-3 bg-[#FAFAFA]"
        >
          <div className="flex items-center justify-between gap-2">
            <div className="text-[12px] font-semibold text-[#0C0B0B]">
              Core output {index + 1}
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                className="p-1.5 rounded hover:bg-white disabled:opacity-30"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                aria-label="Move up"
              >
                <ArrowUp className="w-3.5 h-3.5 text-[#626262]" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-white disabled:opacity-30"
                disabled={index === value.length - 1}
                onClick={() => move(index, 1)}
                aria-label="Move down"
              >
                <ArrowDown className="w-3.5 h-3.5 text-[#626262]" />
              </button>
              <button
                type="button"
                className="p-1.5 rounded hover:bg-white text-red-600"
                onClick={() => onChange(value.filter((_, i) => i !== index))}
                aria-label="Remove"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <div>
            <label className={labelClass}>Name</label>
            <input
              className={fieldClass}
              value={item.name}
              onChange={(e) => updateAt(index, { name: e.target.value })}
            />
          </div>
          <div>
            <label className={labelClass}>Description</label>
            <textarea
              className={`${fieldClass} min-h-[72px]`}
              value={item.text}
              onChange={(e) => updateAt(index, { text: e.target.value })}
            />
          </div>
          <BlobPicker
            label="File"
            value={item.path}
            imageUrl={item.image_url}
            configured={configured}
            container={container}
            onChange={(name, url) =>
              updateAt(index, {
                path: name,
                image_url: url || (name ? item.image_url : ''),
              })
            }
          />
        </div>
      ))}
    </div>
  );
};

export default CoreOutputsEditor;
